import {
    Button,
    ButtonProps,
    Checkbox,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    makeStyles,
    Typography
} from "@material-ui/core";
import * as yup from "yup";
import React, {FC} from "react";
import {useHistory} from "react-router-dom";
import {useEditorLocation} from "../../../editor/editor.hook";
import {ErrorMessage, Form, Formik} from "formik";
import {useCreateOrder} from "../redux/orders.hook";
import {CreateOrderData} from "../redux/types";
import {FormikClientAutoComplete} from "../../clients/sharedComponents/ClientAutoComplete";
import {FormikWorkerAutoComplete} from "../../workers/sharedComponents/WorkerAutoComplete";
import {FormikTextField} from "../../../form/FormikTextField";
import {FormikCheckbox} from "../../../sharedComponents/FormikCheckbox";
import {FormikSelect} from "../../../form/FormikSelect";
import clsx from "clsx";
import {FormikServiceCheckBoxes} from "../../services/redux/FormikServiceCheckBoxes";
import {setHours} from "date-fns";
import {FormikDcbAddressInput} from "../../../sharedComponents/DcbAddressInput";
import {Address} from "../../../redux/types";
import {CreateOrderTimeFormElement} from "./CreateOrderTimeFormElement";

const useStyles = makeStyles(theme => ({
    spacing: {
        marginRight: theme.spacing(1)
    },
    formContainer: {
        paddingBottom: theme.spacing(4)
    },
    select: {
        width: 100
    }
}));

/*function parseDateNumber(_value: any, originalValue: number | undefined | null) {
    return originalValue ? new Date(originalValue) : undefined;
}*/

const validationSchema = yup.object({
    clientId: yup.string().required('erforderlich'),
    workerId: yup.string().required('erforderlich'),
    serviceAddress: yup.object({
        name: yup.object({
            salutation: yup.string().required('erforderlich'),
            firstName: yup.string().required('erforderlich'),
            lastName: yup.string().required('erforderlich')
        }),
        address: yup.object({
            formattedAddress: yup.string().required('erforderlich')
        }),
        phone: yup.string().required('erforderlich')
    }),
    invoiceAddress: yup.object().nullable().default(undefined).shape({
        name: yup.object({
            salutation: yup.string().required('erforderlich'),
            firstName: yup.string().required('erforderlich'),
            lastName: yup.string().required('erforderlich')
        }),
        address: yup.object({
            formattedAddress: yup.string().required('erforderlich')
        }),
        phone: yup.string().required('erforderlich')
    }),
    estimatedFrom: yup.number().typeError("erforderlich").required('erforderlich'),
    estimatedTo: yup.number().typeError("erforderlich").moreThan(yup.ref('estimatedFrom'), 'muss nach Startzeitpunkt sein'),
    billed: yup.boolean()
});

const initialValues: CreateOrderData = {
    clientId: '',
    workerId: '',
    serviceAddress: {
        name: {
            salutation: 'Herr',
            firstName: '',
            lastName: '',
        },
        address: null as unknown as Address,
        phone: '',
    },
    estimatedFrom: null as unknown as number, //new Date().getTime(),
    estimatedTo: null as unknown as number, //new Date().getTime(),
    from: null,
    to: null,
    billed: false,
    servicesIds: [],
    confirmed: false,
};

interface CreateOrderDialogFormProps {
    withoutButton?: boolean,
    initialDate?: Date;
    initialWorkerId?: string,
    initialClientId?: string,
    buttonProps?: ButtonProps
}

export const CreateOrderDialogForm: FC<CreateOrderDialogFormProps> = ({
                                                                          withoutButton,
                                                                          initialDate,
                                                                          initialWorkerId,
                                                                          buttonProps,
                                                                          initialClientId
                                                                      }) => {

    const classes = useStyles();
    const history = useHistory();
    const {mode, reference} = useEditorLocation();
    const {createOrder} = useCreateOrder();

    const [isLoading, setIsLoading] = React.useState(false);
    const dialogOpen = mode === 'create' && reference === 'order';

    const openDialog = () => {
        const newSearchParams = new URLSearchParams(history.location.search);
        newSearchParams.set('mode', 'create');
        history.replace({
            pathname: history.location.pathname,
            search: `${newSearchParams.toString()}`,
            hash: 'order'
        })
    };
    const closeDialog = () => {
        const newSearchParams = new URLSearchParams(history.location.search);
        newSearchParams.delete('mode');
        history.replace({
            pathname: history.location.pathname,
            search: `${newSearchParams.toString()}`,
            hash: undefined
        })
    };

    return (
        <>
            {!withoutButton && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={openDialog}
                    {...buttonProps}
                >
                    Neuer Termin
                </Button>)}
            <Dialog open={dialogOpen} onClose={closeDialog}>
                <DialogTitle>
                    <Typography variant="inherit" color="primary">
                        Neuer Auftrag
                    </Typography>
                </DialogTitle>
                <Formik
                    enableReinitialize
                    initialValues={initialDate ? ({
                        ...initialValues,
                        estimatedFrom: initialDate.getTime(),
                        estimatedTo: setHours(initialDate, initialDate.getHours() + 8).getTime(),
                        workerId: initialWorkerId ?? '',
                        clientId: initialClientId ?? ''
                    }) : ({
                        ...initialValues,
                        workerId: initialWorkerId ?? '',
                        clientId: initialClientId ?? ''
                    })}
                    onSubmit={(dto) => {
                        setIsLoading(true);
                        createOrder(dto)
                            .then(() => {
                                closeDialog();
                            })
                            .catch(() => {
                            })
                            .finally(() => {
                                setIsLoading(false)
                            })
                    }}
                    validationSchema={validationSchema}
                >
                    {({values, submitForm, setFieldValue, resetForm}) => (
                        <Form>
                            <DialogContent className={classes.formContainer}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormikClientAutoComplete
                                            value={values.clientId}
                                            name={"clientId"}
                                            required
                                            onNewClientCreated={(clientId) => setFieldValue('clientId', clientId)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikWorkerAutoComplete name={"workerId"} required/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikServiceCheckBoxes workerId={values.workerId} name={"servicesIds"}
                                                                 label={"Dienstleistungen"}/>
                                    </Grid>
                                    <CreateOrderTimeFormElement/>
                                    <Grid item xs={12}>
                                        <Typography
                                            style={{marginTop: 16, marginBottom: 4}}
                                            color="primary"
                                        >
                                            Einsatzort
                                        </Typography>
                                        <Divider/>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikSelect
                                            name={"serviceAddress.name.salutation"}
                                            label="Anrede"
                                            required
                                            options={[
                                                {value: 'Herr', label: 'Herr'},
                                                {value: 'Frau', label: 'Frau'},
                                                {value: 'Divers', label: 'Divers'},
                                            ]}
                                            className={clsx(classes.spacing, classes.select)}
                                        />
                                        <FormikTextField
                                            name={"serviceAddress.name.firstName"}
                                            label="Vorname"
                                            required
                                            className={classes.spacing}
                                        />
                                        <FormikTextField
                                            required
                                            name={"serviceAddress.name.lastName"}
                                            label="Nachname"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikDcbAddressInput
                                            name={"serviceAddress.address"}
                                            label="Auftrags Adresse"
                                            textFieldProps={{required: true}}
                                        />
                                        <Typography color="error">
                                            <ErrorMessage name={'serviceAddress.address.formattedAddress'}/>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            fullWidth
                                            name={"serviceAddress.phone"}
                                            label="Telefon"
                                            type="phone"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{display: 'flex', alignItems: "flex-end"}}>
                                        <FormControlLabel
                                            onClick={e => e.stopPropagation()}
                                            style={{flex: 1}}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    onClick={e => e.stopPropagation()}
                                                    checked={Boolean(values.invoiceAddress)}
                                                    onChange={(_e, checked) => checked
                                                        ? setFieldValue('invoiceAddress', {
                                                            name: {
                                                                salutation: 'Herr',
                                                                firstName: '',
                                                                lastName: '',
                                                            },
                                                            phone: '',
                                                            address: {
                                                                placeId: '',
                                                                formattedAddress: '',
                                                                long: 0,
                                                                lat: 0
                                                            }
                                                        })
                                                        : setFieldValue('invoiceAddress', undefined)
                                                    }
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="subtitle1"
                                                    color="textSecondary"
                                                >
                                                    Der Einsatzort ist nicht die Rechnungsadresse
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Collapse in={Boolean(values.invoiceAddress)}>
                                            <Typography
                                                style={{marginTop: 16, marginBottom: 4}}
                                                color="primary"
                                            >
                                                Rechnungsadresse
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormikSelect
                                                        name={"invoiceAddress.name.salutation"}
                                                        label="Anrede"
                                                        required
                                                        options={[
                                                            {value: 'Herr', label: 'Herr'},
                                                            {value: 'Frau', label: 'Frau'},
                                                            {value: 'Divers', label: 'Divers'},
                                                        ]}
                                                        className={clsx(classes.spacing, classes.select)}
                                                    />
                                                    <FormikTextField
                                                        name={"invoiceAddress.name.firstName"}
                                                        label="Vorname"
                                                        className={classes.spacing}
                                                    />
                                                    <FormikTextField name={"invoiceAddress.name.lastName"}
                                                                     label="Nachname"/>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormikDcbAddressInput name={"invoiceAddress.address"}
                                                                           label="Rechnungs Addresse"
                                                                           textFieldProps={{required: true}}/>
                                                    {/*<PlacesAutoComplete
                                                        label="Rechnungs Addresse"
                                                        onPlaceSelected={(address) => setFieldValue('invoiceAddress.address', address)}
                                                    />*/}
                                                    <Typography color="error">
                                                        <ErrorMessage name={'invoiceAddress.address.formattedAddress'}/>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormikTextField
                                                        fullWidth
                                                        name={"invoiceAddress.phone"}
                                                        label="Telefon"
                                                        type="phone"
                                                        required
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Collapse>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            fullWidth
                                            name={"extraInfo"}
                                            label="Nachricht an den Mitarbeiter"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikCheckbox name={"billed"} label={"In Rechnung gestellt"}/>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {isLoading ?
                                    <CircularProgress/> :
                                    (<>
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                closeDialog();
                                                resetForm();
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button color="primary" onClick={submitForm}>
                                            Save
                                        </Button>
                                    </>)}
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    )
}
